/* eslint-disable ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-runloop, ember/require-tagless-components */
import Component from '@ember/component';
import { scheduleOnce } from '@ember/runloop';
import { tracked } from '@glimmer/tracking';

// D3 imports
import { scaleBand, scaleLinear, scaleOrdinal } from 'd3-scale';
import { select } from 'd3-selection';
import { schemeCategory10 } from 'd3-scale-chromatic';
import { max } from 'd3-array';
import { axisBottom, axisLeft } from 'd3-axis';
import tip from '@linc-technologies/linced-common/utils/d3-tip';
import { sortBy } from '@linc-technologies/linced-common/utils/arrays';

export default class ChartGroupedBar extends Component {
  width = 1000;
  height = 500;
  @tracked
  chart = null;
  margin = Object.freeze({ top: 10, right: 10, bottom: 120, left: 65 });
  xInterval = [];
  data = [];
  barClicked() {}

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    // Schedule a call to our `draw` method on Ember's "render" queue, which will
    // happen after the component has been placed in the DOM, and subsequently
    // each time data is changed.
    scheduleOnce('render', this, this.draw);
  }

  draw() {
    let timeframes = new Set(this.data.map((d) => d.timeframe));

    let fx = scaleBand()
      .domain(sortBy(this.data, 'levelSequence').map((d) => d.level))
      .rangeRound([this.margin.left, this.width - this.margin.right])
      .paddingInner(0.6);

    // X encodes the width of the bar.
    let x = scaleBand()
      .domain(this.data.map((d) => d.timeframe))
      .rangeRound([0, fx.bandwidth()])
      .padding(0.05)
      .paddingOuter(0.05);

    // Y encodes the height of the bar.
    let y = scaleLinear()
      .domain([0, max(this.data, (d) => d.students.length)])
      .nice()
      .rangeRound([this.height - this.margin.bottom, this.margin.top]);

    // map a color to each milestone
    let color = scaleOrdinal()
      .domain(Array.from(timeframes))
      .range(schemeCategory10);

    // Create the SVG container on the root element.
    let svg = select(this.element)
      .append('svg')
      .attr('width', this.width)
      .attr('height', this.height);

    // Tooltip
    let tooltip = tip()
      .attr('class', 'd3-tip')
      .html((d) => d.tooltip);
    svg.call(tooltip);

    // Grouping the data by 'level'
    let levelGroups = {};
    this.data.forEach((d) => {
      if (!levelGroups[d.level]) {
        levelGroups[d.level] = [];
      }
      levelGroups[d.level].push(d);
    });

    // Convert the object into an array of entries
    let levelEntries = Object.entries(levelGroups);
    let xAxisPositions = [];
    xAxisPositions.push(3);
    let xAxisPosSize = 3;
    for (let i = 1; i < Array.from(timeframes).length; i++) {
      xAxisPosSize += x.bandwidth() + 3;
      xAxisPositions.push(xAxisPosSize);
    }

    // Append a group for each level
    let groups = svg
      .append('g')
      .selectAll('g')
      .data(levelEntries)
      .enter()
      .append('g')
      .attr('transform', ([level]) => `translate(${fx(level)}, 0)`);

    // Append a rectangle for each timeframe within each group
    groups
      .selectAll('rect')
      .data(([, d]) => d)
      .enter()
      .append('rect')
      .attr('x', (d, i, nodes) => {
        let group = select(nodes[i].parentNode).datum()[1];
        return this.positionXAxis(xAxisPositions, i, group);
      })
      .attr('y', (d) => y(d.students.length))
      .attr('width', x.bandwidth())
      .attr('height', (d) => y(0) - y(d.students.length))
      .attr('fill', (d) => color(d.timeframe))
      .on('mouseover', tooltip.show)
      .on('mouseout', tooltip.hide)
      .on('click', (_, d) => this.barClicked(d.students, d.tooltip.slice(2)));

    // Append the horizontal axis.
    svg
      .append('g')
      .attr('transform', `translate(0,${this.height - this.margin.bottom})`)
      .call(axisBottom(fx).tickSizeOuter(0))
      .call((g) => g.selectAll('.domain').remove())
      .selectAll('text')
      .attr('transform', 'rotate(-45)')
      .style('text-anchor', 'end')
      .style('font-weight', 'bold')
      .style('font-size', '12px');

    // Append the vertical axis
    svg
      .append('g')
      .attr('transform', `translate(${this.margin.left},0)`)
      .call(axisLeft(y).ticks(Math.min(10, y.domain()[1])))
      .call((g) => g.selectAll('.domain').remove());

    this.chart = Object.assign(svg.node());
  }

  positionXAxis(xAxisPositions, index, group) {
    if (group.length === xAxisPositions.length) {
      return xAxisPositions[index];
    }
    let startIndex = Math.floor((xAxisPositions.length - group.length) / 2);
    return xAxisPositions[index + startIndex];
  }
}
